/**
 * Compact circular plan-points usage indicator for the sidebar (hover for details).
 *
 * Reads ``pointsUsed`` / ``pointsBudget`` from ``GET /auth/billing/entitlement``,
 * with a fallback to the legacy ``usedTokens`` / ``includedTokenBudget``
 * fields so a pre-M4 entitlement endpoint still renders correctly.
 * Both fields hold the same int post-migration; the fallback only matters
 * during the rolling-deploy window.
 *
 * Hover tooltip uses a React portal + fixed positioning. The ring lives in
 * the sidebar nav rail, where multiple ancestors set ``overflow: hidden``;
 * an in-flow tooltip would clip — z-index doesn't help once the parent
 * clips. The portal escapes the sidebar's DOM subtree entirely, anchored
 * to the trigger via ``getBoundingClientRect()``. This pattern matches
 * ``ui-shared/PortalMenu`` (per the project rule "Always use PortalMenu
 * for any menu/popover/dropdown") but inlined because PortalMenu's
 * outside-click + Escape semantics fit click-driven menus, not hover
 * tooltips.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { fetchOrgBillingEntitlement, isBillingUiEnabled } from '@/services/stripeBilling';
import { useTabFocusRefetch } from '@stepflow/ui-shared/hooks/useTabFocusRefetch.js';
import ManageSubscriptionButton from './ManageSubscriptionButton.jsx';
import {
  normalizeStepflowRole,
  canAccessWorkspaceAdmin,
} from '@/utils/stepflowRoles';

/**
 * Two-layer RBAC gate. The ring shows org-level plan billing data — only
 * users in a billing-management role should see it (or call the endpoint).
 *
 * **Stepflow role** (account-level, on the JWT): OWNER means "the user who
 * created the org". Always sees the ring.
 *
 * **Workspace role** (per-tenant membership from AuthContext): OWNER or
 * ADMIN of the *active* workspace. Workspace admins manage the tenant's
 * billing UX so they need the same view.
 *
 * Anyone else (USER / CREATOR-only / regular workspace MEMBER) gets ``null``
 * — no fetch, no DOM, no UI surface.
 */
function canViewOrgBilling(user, workspaceRole) {
  const stepflow = normalizeStepflowRole(user?.stepflowRole);
  if (stepflow === 'OWNER') return true;
  // canAccessWorkspaceAdmin → OWNER or ADMIN at the workspace tier.
  return canAccessWorkspaceAdmin(workspaceRole);
}

function formatPoints(n) {
  if (typeof n !== 'number' || !isFinite(n)) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

function renewalLabel(periodEndUnix) {
  if (!periodEndUnix) return null;
  try {
    return new Date(periodEndUnix * 1000).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

function Ring({ percent, size, stroke, trackClass, fillClass, children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, percent));
  const offset = c * (1 - pct / 100);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className={trackClass}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className={fillClass}
        />
      </svg>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

const TOOLTIP_WIDTH = 224;
const TOOLTIP_OFFSET = 8; // gap between ring and tooltip
const VIEWPORT_PAD = 8;

/**
 * Portal tooltip pinned to the right edge of ``anchorRef``.
 *
 * "right-aligned" per the operator request: tooltip appears to the
 * right of the ring (sidebar lives on the left of the viewport, so
 * the tooltip flows into the workspace area where there's room).
 * Falls back to "left of the anchor" if the right edge would clip,
 * and to "below" if neither side fits — same flip logic PortalMenu uses.
 */
function HoverTooltip({ anchorRef, open, children, onMouseEnter, onMouseLeave, onRequestClose }) {
  const tipRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });

  // Dismiss on outside-click + Escape. This is what makes the popover
  // "stable until you click outside" — once it's open (whether the
  // pointer is still on the ring or has moved into the tooltip to reach
  // the Manage button), it stays put until the user explicitly clicks
  // elsewhere or presses Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const tip = tipRef.current;
      const anchor = anchorRef.current;
      if (tip && tip.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      onRequestClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onRequestClose?.();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorRef, onRequestClose]);

  const updatePos = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const tip = tipRef.current;
    const tipH = tip ? tip.offsetHeight : 0;
    const tipW = tip ? tip.offsetWidth : TOOLTIP_WIDTH;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Default: place to the right of the ring, vertically centered.
    let left = rect.right + TOOLTIP_OFFSET;
    let top = rect.top + rect.height / 2 - tipH / 2;

    // If the tooltip would overflow the right edge, flip to the left
    // of the ring instead.
    if (left + tipW > vw - VIEWPORT_PAD) {
      left = rect.left - TOOLTIP_OFFSET - tipW;
    }
    // If neither side fits (very narrow viewport), drop below.
    if (left < VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, rect.left);
      top = rect.bottom + TOOLTIP_OFFSET;
    }
    // Clamp vertically.
    if (top + tipH > vh - VIEWPORT_PAD) top = vh - VIEWPORT_PAD - tipH;
    if (top < VIEWPORT_PAD) top = VIEWPORT_PAD;

    setPos({ top, left, ready: true });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) {
      setPos((p) => ({ ...p, ready: false }));
      return undefined;
    }
    updatePos();
    const handler = () => updatePos();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, updatePos]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={tipRef}
      role="tooltip"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: TOOLTIP_WIDTH,
        visibility: pos.ready ? 'visible' : 'hidden',
      }}
      className="z-[1000] rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-600 shadow-lg"
    >
      {children}
    </div>,
    document.body,
  );
}

export default function OrgCreditRing({ compact = false }) {
  const { user, workspaceRole } = useAuth();
  // Billing UI master switch (single-account/no-Stripe deploys set it false).
  // Folded into ``allowed`` so the entitlement fetch is skipped too, not just
  // the DOM.
  const allowed = isBillingUiEnabled() && canViewOrgBilling(user, workspaceRole);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [hovered, setHovered] = useState(false);
  const anchorRef = useRef(null);
  // Grace-delay timer so the pointer can travel across the gap between
  // the ring and the tooltip (which sits TOOLTIP_OFFSET px away) without
  // the tooltip closing mid-traverse. Entering either the ring or the
  // tooltip cancels a pending close.
  const closeTimer = useRef(null);

  const openTip = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setHovered(true);
  }, []);

  const scheduleClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    // ~120ms is enough to cross an 8px gap without feeling sticky.
    closeTimer.current = setTimeout(() => setHovered(false), 120);
  }, []);

  const closeNow = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setHovered(false);
  }, []);

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  // Only fetch the entitlement when the user is in a payer role. This
  // is both a UX gate AND a "don't leak plan data to non-payer users"
  // posture — no network call, no shape in localStorage, no error path
  // that would briefly flash budget data.
  const refresh = useCallback(async () => {
    if (!allowed) {
      setLoading(false);
      setData(null);
      return;
    }
    try {
      const d = await fetchOrgBillingEntitlement();
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refresh();
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  // PR 6d — re-fetch when the user returns to the StepFlow tab from the
  // Stripe Billing Portal. Plan changes apply at Stripe immediately
  // (upgrade) or at period end (downgrade) but our entitlement-row
  // mirror only updates via webhook; the visibilitychange refetch is
  // what bridges the gap so the ring reflects the new plan as soon as
  // the webhook has landed (typically within a few seconds of the
  // Portal action).
  useTabFocusRefetch(refresh, allowed);

  if (!allowed) return null;
  if (loading || !data?.orgId) return null;
  if (!data.hasEntitlement) return null;

  const size = compact ? 36 : 44;
  const stroke = compact ? 3 : 3.5;

  // Common hover-target shell — keeps the same DOM hooks for the
  // unlimited and finite paths.
  const HoverWrap = ({ children, ariaLabel, tooltipBody }) => (
    <div className="relative flex justify-center py-1">
      <div
        ref={anchorRef}
        className="relative cursor-default"
        role="img"
        aria-label={ariaLabel}
        onMouseEnter={openTip}
        onMouseLeave={scheduleClose}
        onFocus={openTip}
        onBlur={scheduleClose}
        tabIndex={0}
      >
        {children}
      </div>
      <HoverTooltip
        anchorRef={anchorRef}
        open={hovered}
        onMouseEnter={openTip}
        onMouseLeave={scheduleClose}
        onRequestClose={closeNow}
      >
        {tooltipBody}
      </HoverTooltip>
    </div>
  );

  if (data.unlimited) {
    return (
      <HoverWrap
        ariaLabel="Pay-as-you-go — usage metered on your invoice"
        tooltipBody={
          <>
            <p className="font-semibold text-slate-800">Pay-as-you-go</p>
            <p className="mt-1 leading-snug">
              Usage is metered; overages appear on your Stripe invoice.
            </p>
            {renewalLabel(data.periodEndUnix) ? (
              <p className="mt-1.5 text-slate-500">
                Period ends {renewalLabel(data.periodEndUnix)}
              </p>
            ) : null}
            <div className="pointer-events-auto mt-2">
              <ManageSubscriptionButton allowed={allowed} returnPath="/billing" />
            </div>
          </>
        }
      >
        <Ring
          percent={100}
          size={size}
          stroke={stroke}
          trackClass="stroke-slate-200"
          fillClass="stroke-emerald-400"
        >
          <span className="text-[11px] font-semibold text-emerald-700" aria-hidden>
            ∞
          </span>
        </Ring>
      </HoverWrap>
    );
  }

  const pct = typeof data.percentUsed === 'number' ? data.percentUsed : 0;
  // ``pointsUsed`` / ``pointsBudget`` are the canonical post-M4 fields.
  // Legacy ``usedTokens`` / ``includedTokenBudget`` are kept as aliases
  // server-side and fall through cleanly if the endpoint is on an older
  // build during a rolling deploy.
  const used = data.pointsUsed ?? data.usedTokens ?? 0;
  const cap = data.pointsBudget ?? data.includedTokenBudget ?? 0;
  const warn = data.warningLevel;
  const fillClass =
    warn === 'critical'
      ? 'stroke-rose-500'
      : warn === 'high'
        ? 'stroke-amber-500'
        : 'stroke-emerald-500';

  return (
    <HoverWrap
      ariaLabel={`Plan points ${pct.toFixed(0)} percent used`}
      tooltipBody={
        <>
          <p className="font-semibold text-slate-800">Plan points</p>
          <p className="mt-1 tabular-nums">
            {formatPoints(used)} / {formatPoints(cap)}{' '}
            <span className="text-slate-400">({data.usageMonth})</span>
          </p>
          {renewalLabel(data.periodResetUnix ?? data.periodEndUnix) ? (
            <p className="mt-1 text-slate-500">
              Renews {renewalLabel(data.periodResetUnix ?? data.periodEndUnix)}
            </p>
          ) : null}
          <p className="mt-1.5 tabular-nums font-medium text-slate-700">
            {pct.toFixed(0)}% used
          </p>
          {warn === 'high' || warn === 'critical' ? (
            <p className="mt-2 border-t border-slate-100 pt-2 text-[11px] leading-snug text-slate-600">
              {warn === 'critical'
                ? 'Plan points are nearly exhausted.'
                : 'You are approaching your plan-points limit.'}
            </p>
          ) : null}
          {warn === 'critical' ? (
            <Link
              to="/pricing"
              className="pointer-events-auto mt-2 inline-block text-xs font-medium text-rose-700 underline"
            >
              View plans
            </Link>
          ) : null}
          {/* Manage subscription is the always-on entry point to the
              Stripe Portal. Surfaced in the tooltip so a user noticing
              their usage trend can act without leaving the workbench. */}
          <div className="pointer-events-auto mt-2">
            <ManageSubscriptionButton allowed={allowed} returnPath="/billing" />
          </div>
        </>
      }
    >
      <Ring
        percent={pct}
        size={size}
        stroke={stroke}
        trackClass="stroke-slate-200"
        fillClass={fillClass}
      >
        <span
          className={`text-[10px] font-semibold tabular-nums leading-none ${
            warn === 'critical'
              ? 'text-rose-700'
              : warn === 'high'
                ? 'text-amber-800'
                : 'text-slate-700'
          }`}
        >
          {Math.round(pct)}%
        </span>
      </Ring>
    </HoverWrap>
  );
}
